/**
 * Achilles Filter V2 - JavaScript Controller
 * Handles sheet interactions, tab switching, and filter application
 */
(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════
  var CONFIG = {
    selectors: {
      container: '[data-achilles-filter-v2]',
      openSheet: '[data-open-sheet]',
      sheet: '[data-sheet]',
      backdrop: '[data-sheet-backdrop]',
      closeSheet: '[data-sheet-close]',
      handle: '[data-sheet-handle]',
      tab: '[data-tab]',
      panel: '[data-panel]',
      filterInput: '[data-filter-input]',
      sortInput: '[data-sort-input]',
      clearFilters: '[data-clear-filters]',
      applyFilters: '[data-apply-filters]',
      resultCount: '[data-result-count]',
      priceMin: '[data-price-min]',
      priceMax: '[data-price-max]',
      pagination: '.pagination a'
    },
    classes: {
      active: 'is-active',
      selected: 'is-selected',
      loading: 'is-loading',
      bodyLock: 'achilles-v2-sheet-open'
    },
    dragThreshold: 100
  };

  // ═══════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════
  var state = {
    activeSheet: null,
    activeTab: null,
    isDragging: false,
    dragStartY: 0,
    sheetStartY: 0,
    scrollY: 0
  };

  // ═══════════════════════════════════════════════════════════
  // SHEET MANAGEMENT
  // ═══════════════════════════════════════════════════════════
  function openSheet(sheetId) {
    var sheet = document.querySelector('[data-sheet="' + sheetId + '"]');
    var backdrop = document.querySelector('[data-sheet-backdrop="' + sheetId + '"]');

    if (!sheet || !backdrop) return;

    // Store scroll position and lock body
    state.scrollY = window.scrollY;
    document.body.style.top = '-' + state.scrollY + 'px';
    document.body.classList.add(CONFIG.classes.bodyLock);

    // Activate sheet and backdrop
    sheet.classList.add(CONFIG.classes.active);
    backdrop.classList.add(CONFIG.classes.active);

    state.activeSheet = sheetId;

    // Focus first interactive element
    setTimeout(function() {
      var firstFocusable = sheet.querySelector('button, input, [tabindex="0"]');
      if (firstFocusable) firstFocusable.focus();
    }, 350);
  }

  function closeSheet(sheetId) {
    var sheet = document.querySelector('[data-sheet="' + sheetId + '"]');
    var backdrop = document.querySelector('[data-sheet-backdrop="' + sheetId + '"]');

    if (!sheet || !backdrop) return;

    // Deactivate sheet and backdrop
    sheet.classList.remove(CONFIG.classes.active);
    backdrop.classList.remove(CONFIG.classes.active);

    // Clear inline transform from dragging
    sheet.style.transform = '';

    // Restore body scroll
    document.body.classList.remove(CONFIG.classes.bodyLock);
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY);

    state.activeSheet = null;
  }

  // ═══════════════════════════════════════════════════════════
  // DRAG TO CLOSE
  // ═══════════════════════════════════════════════════════════
  function handleDragStart(e) {
    var handle = e.target.closest(CONFIG.selectors.handle);
    if (!handle) return;

    state.isDragging = true;
    state.dragStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    state.sheetStartY = 0;

    var sheet = handle.closest(CONFIG.selectors.sheet);
    if (sheet) {
      sheet.style.transition = 'none';
    }
  }

  function handleDragMove(e) {
    if (!state.isDragging) return;

    var clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    var deltaY = clientY - state.dragStartY;

    // Only allow dragging down
    if (deltaY < 0) deltaY = 0;

    var sheet = document.querySelector('[data-sheet="' + state.activeSheet + '"]');
    if (sheet) {
      // Check if on desktop (centered sheet)
      var isDesktop = window.innerWidth >= 769;
      if (isDesktop) {
        sheet.style.transform = 'translateX(-50%) translateY(' + deltaY + 'px)';
      } else {
        sheet.style.transform = 'translateY(' + deltaY + 'px)';
      }
    }
  }

  function handleDragEnd(e) {
    if (!state.isDragging) return;

    var clientY = e.type === 'touchend' ? e.changedTouches[0].clientY : e.clientY;
    var deltaY = clientY - state.dragStartY;

    var sheet = document.querySelector('[data-sheet="' + state.activeSheet + '"]');
    if (sheet) {
      sheet.style.transition = '';

      if (deltaY > CONFIG.dragThreshold) {
        closeSheet(state.activeSheet);
      } else {
        // Snap back
        var isDesktop = window.innerWidth >= 769;
        if (isDesktop) {
          sheet.style.transform = 'translateX(-50%) translateY(0)';
        } else {
          sheet.style.transform = 'translateY(0)';
        }
      }
    }

    state.isDragging = false;
  }

  // ═══════════════════════════════════════════════════════════
  // TAB SWITCHING
  // ═══════════════════════════════════════════════════════════
  function switchTab(tabId) {
    var container = document.querySelector(CONFIG.selectors.container);
    if (!container) return;

    // Update tab states
    var tabs = container.querySelectorAll(CONFIG.selectors.tab);
    tabs.forEach(function(tab) {
      var isActive = tab.dataset.tab === tabId;
      tab.classList.toggle(CONFIG.classes.active, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update panel states with crossfade
    var panels = container.querySelectorAll(CONFIG.selectors.panel);
    panels.forEach(function(panel) {
      var isActive = panel.dataset.panel === tabId;
      if (isActive) {
        panel.hidden = false;
        // Trigger reflow for animation
        panel.offsetHeight;
        panel.classList.add(CONFIG.classes.active);
      } else {
        panel.classList.remove(CONFIG.classes.active);
        setTimeout(function() {
          if (!panel.classList.contains(CONFIG.classes.active)) {
            panel.hidden = true;
          }
        }, 200);
      }
    });

    state.activeTab = tabId;
  }

  // ═══════════════════════════════════════════════════════════
  // FILTER APPLICATION
  // ═══════════════════════════════════════════════════════════
  function buildFilterURL(pageNumber) {
    var container = document.querySelector(CONFIG.selectors.container);
    if (!container) return window.location.pathname;

    var params = new URLSearchParams();

    // Collect all checked filter inputs
    var inputs = container.querySelectorAll(CONFIG.selectors.filterInput);
    inputs.forEach(function(input) {
      if (input.type === 'checkbox' && input.checked) {
        params.append(input.name, input.value);
      } else if (input.type === 'number' && input.value) {
        params.append(input.name, input.value);
      }
    });

    // Preserve sort parameter
    var urlParams = new URLSearchParams(window.location.search);
    var sortBy = urlParams.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    // Add page parameter if specified
    if (pageNumber && pageNumber > 1) {
      params.set('page', pageNumber);
    }

    var queryString = params.toString();
    return window.location.pathname + (queryString ? '?' + queryString : '');
  }

  // Build URL preserving current filters but changing the page
  function buildPaginationURL(paginationUrl) {
    // Extract page number from pagination URL
    var paginationParams = new URLSearchParams(paginationUrl.split('?')[1] || '');
    var pageNumber = paginationParams.get('page') || 1;

    // Get current URL params (which have the filters)
    var currentParams = new URLSearchParams(window.location.search);

    // Update page number
    if (pageNumber && pageNumber > 1) {
      currentParams.set('page', pageNumber);
    } else {
      currentParams.delete('page');
    }

    var queryString = currentParams.toString();
    return window.location.pathname + (queryString ? '?' + queryString : '');
  }

  function applyFilters() {
    // Reset to page 1 when applying new filters
    var url = buildFilterURL();
    fetchAndRenderCollection(url);
    closeSheet('filter');
  }

  function applySort(sortValue) {
    var params = new URLSearchParams(window.location.search);
    params.set('sort_by', sortValue);
    // Reset to page 1 when changing sort
    params.delete('page');

    var url = window.location.pathname + '?' + params.toString();
    fetchAndRenderCollection(url);
    closeSheet('sort');
  }

  function clearFilters() {
    var container = document.querySelector(CONFIG.selectors.container);
    if (!container) return;

    // Uncheck all checkboxes
    var checkboxes = container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(function(cb) {
      cb.checked = false;
      var label = cb.closest('label');
      if (label) label.classList.remove(CONFIG.classes.selected);
    });

    // Reset price inputs
    var priceInputs = container.querySelectorAll(CONFIG.selectors.filterInput + '[type="number"]');
    priceInputs.forEach(function(input) {
      input.value = input.min || '';
    });
  }

  function clearAllFilters() {
    var params = new URLSearchParams(window.location.search);
    var sortBy = params.get('sort_by');

    var url = window.location.pathname;
    if (sortBy) {
      url += '?sort_by=' + sortBy;
    }

    fetchAndRenderCollection(url);
    closeSheet('filter');
  }

  // ═══════════════════════════════════════════════════════════
  // AJAX COLLECTION FETCH
  // ═══════════════════════════════════════════════════════════
  function fetchAndRenderCollection(url) {
    var container = document.querySelector(CONFIG.selectors.container);
    if (container) {
      container.classList.add(CONFIG.classes.loading);
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(xhr.responseText, 'text/html');

        // Update product grid - replace entire wrapper to preserve structure
        var newGridWrapper = doc.querySelector('.collection-grid__wrapper');
        var currentGridWrapper = document.querySelector('.collection-grid__wrapper');
        if (newGridWrapper && currentGridWrapper) {
          currentGridWrapper.innerHTML = newGridWrapper.innerHTML;
        }

        // Update pagination
        var newPagination = doc.querySelector('.pagination');
        var currentPagination = document.querySelector('.pagination');
        if (newPagination && currentPagination) {
          currentPagination.outerHTML = newPagination.outerHTML;
        } else if (newPagination && !currentPagination) {
          // Add pagination if it didn't exist before
          var gridWrapper = document.querySelector('.collection-grid__wrapper');
          if (gridWrapper && gridWrapper.parentNode) {
            gridWrapper.parentNode.insertAdjacentHTML('beforeend', newPagination.outerHTML);
          }
        } else if (!newPagination && currentPagination) {
          // Remove pagination if no longer needed
          currentPagination.remove();
        }

        // Update URL
        window.history.pushState({}, '', url);

        // CRITICAL: Reinitialize AOS with refreshHard to make images visible
        // AOS is initialized with disableMutationObserver: true, so we must manually refresh
        if (typeof AOS !== 'undefined') {
          AOS.refreshHard();
        }

        // Call theme's reinitProductGridItem which handles AOS, quick shop, etc.
        if (typeof theme !== 'undefined' && theme.reinitProductGridItem) {
          theme.reinitProductGridItem();
        }

        // Re-initialize lazy loading for images
        reinitializeLazyLoading();

        // Dispatch event for other scripts
        document.dispatchEvent(new CustomEvent('collection:reloaded'));

        // Re-initialize this filter component
        init();

        // Reinitialize theme sections if available
        if (typeof theme !== 'undefined' && theme.sections) {
          theme.sections.reinit('collection');
        }
      }

      // Remove loading state from new container
      var newContainer = document.querySelector(CONFIG.selectors.container);
      if (newContainer) {
        newContainer.classList.remove(CONFIG.classes.loading);
      }
    };

    xhr.onerror = function() {
      // Fallback to full page load
      window.location.href = url;
    };

    xhr.send();
  }

  // Re-initialize lazy loading after DOM update
  function reinitializeLazyLoading() {
    // Force all .image-element images to be visible (they start with opacity: 0)
    // The AOS animation sets opacity via animation, but we need to ensure visibility
    var imageElements = document.querySelectorAll('.collection-grid__wrapper .image-element');
    imageElements.forEach(function(img) {
      // Add inline style to ensure visibility
      img.style.opacity = '1';
    });

    // Also add loaded class to image-wrap elements
    var imageWraps = document.querySelectorAll('.collection-grid__wrapper .image-wrap');
    imageWraps.forEach(function(wrap) {
      wrap.classList.add('loaded');
    });

    // Handle native lazy loading - force load by removing and re-adding loading attribute
    var lazyImages = document.querySelectorAll('.collection-grid__wrapper img[loading="lazy"]');
    lazyImages.forEach(function(img) {
      // Trigger load by updating src
      var src = img.src;
      if (src) {
        img.src = '';
        img.src = src;
      }
      // Also handle srcset
      var srcset = img.srcset;
      if (srcset) {
        img.srcset = '';
        img.srcset = srcset;
      }
    });

    // Handle lazysizes library if present
    if (typeof lazySizes !== 'undefined') {
      lazySizes.init();
    }

    // Handle theme's custom lazy loading
    if (typeof theme !== 'undefined' && theme.lazyLoad) {
      theme.lazyLoad.update();
    }
  }

  // ═══════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════
  function handleClick(e) {
    // Open sheet
    var openBtn = e.target.closest(CONFIG.selectors.openSheet);
    if (openBtn) {
      e.preventDefault();
      openSheet(openBtn.dataset.openSheet);
      return;
    }

    // Close sheet (backdrop or close button)
    var backdrop = e.target.closest(CONFIG.selectors.backdrop);
    if (backdrop && backdrop.classList.contains(CONFIG.classes.active)) {
      closeSheet(backdrop.dataset.sheetBackdrop);
      return;
    }

    var closeBtn = e.target.closest(CONFIG.selectors.closeSheet);
    if (closeBtn) {
      e.preventDefault();
      closeSheet(closeBtn.dataset.sheetClose);
      return;
    }

    // Tab switch
    var tab = e.target.closest(CONFIG.selectors.tab);
    if (tab) {
      e.preventDefault();
      switchTab(tab.dataset.tab);
      return;
    }

    // Clear filters
    var clearBtn = e.target.closest(CONFIG.selectors.clearFilters);
    if (clearBtn) {
      e.preventDefault();
      clearAllFilters();
      return;
    }

    // Apply filters
    var applyBtn = e.target.closest(CONFIG.selectors.applyFilters);
    if (applyBtn) {
      e.preventDefault();
      applyFilters();
      return;
    }

    // Size/color/list item click - toggle selection visually
    var sizeLabel = e.target.closest('.achilles-v2__size');
    if (sizeLabel && !sizeLabel.classList.contains('is-disabled')) {
      var input = sizeLabel.querySelector('input');
      if (input) {
        sizeLabel.classList.toggle(CONFIG.classes.selected, input.checked);
        // Auto-activate "In Stock" when size is selected
        if (input.checked) {
          autoActivateInStock();
        }
      }
    }

    var colorLabel = e.target.closest('.achilles-v2__color');
    if (colorLabel) {
      var input = colorLabel.querySelector('input');
      if (input) {
        colorLabel.classList.toggle(CONFIG.classes.selected, input.checked);
        // Auto-activate "In Stock" when color is selected
        if (input.checked) {
          autoActivateInStock();
        }
      }
    }

    var listItem = e.target.closest('.achilles-v2__list-item');
    if (listItem) {
      var input = listItem.querySelector('input');
      if (input) {
        listItem.classList.toggle(CONFIG.classes.selected, input.checked);
      }
    }

    // Pagination link click - preserve filters
    var paginationLink = e.target.closest(CONFIG.selectors.pagination);
    if (paginationLink) {
      e.preventDefault();
      var paginationUrl = paginationLink.getAttribute('href');
      var url = buildPaginationURL(paginationUrl);
      fetchAndRenderCollection(url);
      // Scroll to top of collection
      var collectionWrapper = document.querySelector('.collection-grid__wrapper');
      if (collectionWrapper) {
        collectionWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
  }

  // Auto-activate "In Stock" filter when size or color is selected
  function autoActivateInStock() {
    var container = document.querySelector(CONFIG.selectors.container);
    if (!container) return;

    // Find the "In Stock" checkbox - look for availability filter with "In stock" value
    var inStockInputs = container.querySelectorAll('input[type="checkbox"][data-filter-input]');
    inStockInputs.forEach(function(input) {
      // Check if this is the "In Stock" option by checking the value or label
      var label = input.closest('label');
      if (label) {
        var labelText = label.textContent.toLowerCase();
        if (labelText.includes('in stock') || input.value === '1' || input.value.toLowerCase() === 'in stock') {
          if (!input.checked) {
            input.checked = true;
            label.classList.add(CONFIG.classes.selected);
          }
        }
      }
    });
  }

  function handleChange(e) {
    // Sort input change - apply immediately
    var sortInput = e.target.closest(CONFIG.selectors.sortInput);
    if (sortInput) {
      // Update visual selection
      var sortOptions = document.querySelectorAll('.achilles-v2__sort-option');
      sortOptions.forEach(function(opt) {
        var input = opt.querySelector('input');
        opt.classList.toggle(CONFIG.classes.selected, input && input.checked);
      });

      applySort(sortInput.value);
      return;
    }

    // Filter checkbox change - update visual state
    var filterInput = e.target.closest(CONFIG.selectors.filterInput);
    if (filterInput && filterInput.type === 'checkbox') {
      var label = filterInput.closest('label');
      if (label) {
        label.classList.toggle(CONFIG.classes.selected, filterInput.checked);
      }
    }
  }

  function handleKeydown(e) {
    // Escape closes active sheet
    if (e.key === 'Escape' && state.activeSheet) {
      closeSheet(state.activeSheet);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // PRICE SLIDER
  // ═══════════════════════════════════════════════════════════
  var priceSliderState = {
    isDragging: false,
    activeThumb: null,
    slider: null,
    minThumb: null,
    maxThumb: null,
    rangeBar: null,
    minInput: null,
    maxInput: null,
    minDisplay: null,
    maxDisplay: null,
    rangeMin: 0,
    rangeMax: 1000,
    currency: 'EGP'
  };

  function initPriceSliders() {
    var sliders = document.querySelectorAll('[data-price-slider]');
    sliders.forEach(function(slider) {
      var container = slider.closest('[data-price-range]');
      if (!container) return;

      var rangeMin = parseFloat(container.dataset.rangeMin) || 0;
      var rangeMax = parseFloat(container.dataset.rangeMax) || 10000;
      var currency = container.dataset.currency || 'EGP';

      var minThumb = slider.querySelector('[data-price-thumb="min"]');
      var maxThumb = slider.querySelector('[data-price-thumb="max"]');
      var rangeBar = slider.querySelector('[data-price-range-bar]');
      var minInput = container.querySelector('[data-price-min]');
      var maxInput = container.querySelector('[data-price-max]');
      var minDisplay = slider.querySelector('[data-price-display="min"]');
      var maxDisplay = slider.querySelector('[data-price-display="max"]');

      if (!minThumb || !maxThumb || !minInput || !maxInput) return;

      // Set initial positions based on input values
      var currentMin = parseFloat(minInput.value) || rangeMin;
      var currentMax = parseFloat(maxInput.value) || rangeMax;

      var minPercent = ((currentMin - rangeMin) / (rangeMax - rangeMin)) * 100;
      var maxPercent = ((currentMax - rangeMin) / (rangeMax - rangeMin)) * 100;

      minThumb.style.left = minPercent + '%';
      maxThumb.style.left = maxPercent + '%';
      updateRangeBar(rangeBar, minPercent, maxPercent);

      // Store data on elements
      slider._priceData = {
        rangeMin: rangeMin,
        rangeMax: rangeMax,
        currency: currency,
        minThumb: minThumb,
        maxThumb: maxThumb,
        rangeBar: rangeBar,
        minInput: minInput,
        maxInput: maxInput,
        minDisplay: minDisplay,
        maxDisplay: maxDisplay
      };

      // Add event listeners to thumbs
      minThumb.addEventListener('mousedown', function(e) { startDrag(e, slider, 'min'); });
      minThumb.addEventListener('touchstart', function(e) { startDrag(e, slider, 'min'); }, { passive: false });

      maxThumb.addEventListener('mousedown', function(e) { startDrag(e, slider, 'max'); });
      maxThumb.addEventListener('touchstart', function(e) { startDrag(e, slider, 'max'); }, { passive: false });

      // Sync inputs with slider
      minInput.addEventListener('change', function() {
        syncSliderFromInput(slider, 'min');
      });
      maxInput.addEventListener('change', function() {
        syncSliderFromInput(slider, 'max');
      });
    });
  }

  function startDrag(e, slider, thumbType) {
    e.preventDefault();
    e.stopPropagation();

    var data = slider._priceData;
    if (!data) return;

    priceSliderState.isDragging = true;
    priceSliderState.activeThumb = thumbType;
    priceSliderState.slider = slider;

    var thumb = thumbType === 'min' ? data.minThumb : data.maxThumb;
    thumb.classList.add('is-dragging');

    document.addEventListener('mousemove', handleSliderDrag);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchmove', handleSliderDrag, { passive: false });
    document.addEventListener('touchend', endDrag);
  }

  function handleSliderDrag(e) {
    if (!priceSliderState.isDragging || !priceSliderState.slider) return;

    e.preventDefault();

    var slider = priceSliderState.slider;
    var data = slider._priceData;
    var thumbType = priceSliderState.activeThumb;

    var rect = slider.getBoundingClientRect();
    var clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    var percent = ((clientX - rect.left) / rect.width) * 100;

    // Clamp between 0 and 100
    percent = Math.max(0, Math.min(100, percent));

    // Get other thumb position
    var minPercent = parseFloat(data.minThumb.style.left) || 0;
    var maxPercent = parseFloat(data.maxThumb.style.left) || 100;

    // Prevent thumbs from crossing
    if (thumbType === 'min') {
      percent = Math.min(percent, maxPercent - 2);
      data.minThumb.style.left = percent + '%';
      minPercent = percent;
    } else {
      percent = Math.max(percent, minPercent + 2);
      data.maxThumb.style.left = percent + '%';
      maxPercent = percent;
    }

    // Update range bar
    updateRangeBar(data.rangeBar, minPercent, maxPercent);

    // Calculate actual values
    var range = data.rangeMax - data.rangeMin;
    var minValue = Math.round(data.rangeMin + (minPercent / 100) * range);
    var maxValue = Math.round(data.rangeMin + (maxPercent / 100) * range);

    // Update inputs
    data.minInput.value = minValue;
    data.maxInput.value = maxValue;

    // Update displays
    if (data.minDisplay) data.minDisplay.textContent = data.currency + minValue;
    if (data.maxDisplay) data.maxDisplay.textContent = data.currency + maxValue;
  }

  function endDrag() {
    if (priceSliderState.slider && priceSliderState.activeThumb) {
      var data = priceSliderState.slider._priceData;
      var thumb = priceSliderState.activeThumb === 'min' ? data.minThumb : data.maxThumb;
      thumb.classList.remove('is-dragging');
    }

    priceSliderState.isDragging = false;
    priceSliderState.activeThumb = null;
    priceSliderState.slider = null;

    document.removeEventListener('mousemove', handleSliderDrag);
    document.removeEventListener('mouseup', endDrag);
    document.removeEventListener('touchmove', handleSliderDrag);
    document.removeEventListener('touchend', endDrag);
  }

  function updateRangeBar(rangeBar, minPercent, maxPercent) {
    if (!rangeBar) return;
    rangeBar.style.left = minPercent + '%';
    rangeBar.style.width = (maxPercent - minPercent) + '%';
  }

  function syncSliderFromInput(slider, type) {
    var data = slider._priceData;
    if (!data) return;

    var minValue = parseFloat(data.minInput.value) || data.rangeMin;
    var maxValue = parseFloat(data.maxInput.value) || data.rangeMax;

    // Clamp values
    minValue = Math.max(data.rangeMin, Math.min(minValue, maxValue - 1));
    maxValue = Math.min(data.rangeMax, Math.max(maxValue, minValue + 1));

    // Update inputs
    data.minInput.value = minValue;
    data.maxInput.value = maxValue;

    // Calculate percentages
    var range = data.rangeMax - data.rangeMin;
    var minPercent = ((minValue - data.rangeMin) / range) * 100;
    var maxPercent = ((maxValue - data.rangeMin) / range) * 100;

    // Update thumbs
    data.minThumb.style.left = minPercent + '%';
    data.maxThumb.style.left = maxPercent + '%';

    // Update range bar
    updateRangeBar(data.rangeBar, minPercent, maxPercent);

    // Update displays
    if (data.minDisplay) data.minDisplay.textContent = data.currency + Math.round(minValue);
    if (data.maxDisplay) data.maxDisplay.textContent = data.currency + Math.round(maxValue);
  }

  // ═══════════════════════════════════════════════════════════
  // INFINITE SCROLL
  // ═══════════════════════════════════════════════════════════
  var InfiniteScroll = {
    config: {
      batchSize: 8,
      triggerMargin: '200px',
      throttleMs: 500,
      maxProducts: 500
    },

    state: {
      currentPage: 1,
      totalPages: 1,
      totalProducts: 0,
      isLoading: false,
      hasMore: true,
      observer: null,
      lastLoadTime: 0,
      container: null,
      collectionUrl: ''
    },

    selectors: {
      container: '[data-infinite-scroll]',
      sentinel: '[data-scroll-sentinel]',
      loader: '[data-scroll-loader]',
      endMessage: '[data-scroll-end]',
      grid: '.collection-grid__wrapper .grid'
    },

    init: function() {
      var container = document.querySelector(this.selectors.container);
      if (!container) return;

      // Parse data attributes
      this.state.container = container;
      this.state.totalProducts = parseInt(container.dataset.total) || 0;
      this.state.currentPage = parseInt(container.dataset.currentPage) || 1;
      this.state.totalPages = parseInt(container.dataset.totalPages) || 1;
      this.state.collectionUrl = container.dataset.collectionUrl || window.location.pathname;
      this.config.batchSize = parseInt(container.dataset.batchSize) || 8;

      // Check if there are more products to load
      this.state.hasMore = this.state.currentPage < this.state.totalPages;

      // Hide end message on init (should only show after user scrolls to end)
      var endMessage = document.querySelector(this.selectors.endMessage);
      if (endMessage) {
        endMessage.hidden = true;
      }

      // Setup IntersectionObserver (even for single-page collections)
      // It will show end message only when user scrolls to the sentinel
      this.setupObserver();
    },

    setupObserver: function() {
      var self = this;
      var sentinel = document.querySelector(this.selectors.sentinel);

      if (!sentinel || !('IntersectionObserver' in window)) return;

      // Disconnect existing observer
      if (this.state.observer) {
        this.state.observer.disconnect();
      }

      this.state.observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && !self.state.isLoading) {
            if (self.state.hasMore) {
              self.loadNextBatch();
            } else {
              // User scrolled to end and no more products - show end message
              self.showEndMessage();
            }
          }
        });
      }, {
        root: null,
        rootMargin: this.config.triggerMargin,
        threshold: 0
      });

      this.state.observer.observe(sentinel);
    },

    loadNextBatch: function() {
      var self = this;
      var now = Date.now();

      // Throttle requests
      if (now - this.state.lastLoadTime < this.config.throttleMs) {
        return;
      }

      if (!this.state.hasMore || this.state.isLoading) {
        return;
      }

      this.state.isLoading = true;
      this.state.lastLoadTime = now;
      this.showLoader();

      // Build URL for next page
      var nextPage = this.state.currentPage + 1;
      var url = this.buildPageUrl(nextPage);

      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          self.appendProducts(xhr.responseText);
          self.state.currentPage = nextPage;
          self.state.hasMore = nextPage < self.state.totalPages;

          if (!self.state.hasMore) {
            self.showEndMessage();
          }
        }

        self.state.isLoading = false;
        self.hideLoader();
      };

      xhr.onerror = function() {
        self.state.isLoading = false;
        self.hideLoader();
        console.error('Infinite scroll: Failed to load page', nextPage);
      };

      xhr.send();
    },

    buildPageUrl: function(page) {
      var url = new URL(this.state.collectionUrl, window.location.origin);

      // Preserve existing query params (filters, sort)
      var currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach(function(value, key) {
        if (key !== 'page') {
          url.searchParams.set(key, value);
        }
      });

      // Add page parameter
      url.searchParams.set('page', page);

      return url.toString();
    },

    appendProducts: function(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      // Find new product items
      var newGrid = doc.querySelector(this.selectors.grid);
      var currentGrid = document.querySelector(this.selectors.grid);

      if (!newGrid || !currentGrid) return;

      // Get all new product items
      var newItems = newGrid.querySelectorAll('.grid__item');

      // Append each item to current grid
      newItems.forEach(function(item) {
        currentGrid.appendChild(item.cloneNode(true));
      });

      // Reinitialize components for new items
      this.reinitializeComponents();
    },

    reinitializeComponents: function() {
      // Refresh AOS animations
      if (typeof AOS !== 'undefined') {
        AOS.refreshHard();
      }

      // Reinitialize theme components
      if (typeof theme !== 'undefined' && theme.reinitProductGridItem) {
        theme.reinitProductGridItem();
      }

      // Reinitialize lazy loading
      reinitializeLazyLoading();

      // Dispatch event for other scripts
      document.dispatchEvent(new CustomEvent('collection:productsAppended'));
    },

    showLoader: function() {
      var loader = document.querySelector(this.selectors.loader);
      if (loader) {
        loader.classList.add('is-loading');
      }
    },

    hideLoader: function() {
      var loader = document.querySelector(this.selectors.loader);
      if (loader) {
        loader.classList.remove('is-loading');
      }
    },

    showEndMessage: function() {
      var endMessage = document.querySelector(this.selectors.endMessage);
      var loader = document.querySelector(this.selectors.loader);

      if (loader) {
        loader.style.display = 'none';
      }

      if (endMessage) {
        endMessage.hidden = false;
      }

      // Disconnect observer
      if (this.state.observer) {
        this.state.observer.disconnect();
      }
    },

    reset: function() {
      // Reset state for filter changes
      this.state.currentPage = 1;
      this.state.isLoading = false;
      this.state.hasMore = true;

      // Hide end message
      var endMessage = document.querySelector(this.selectors.endMessage);
      if (endMessage) {
        endMessage.hidden = true;
      }

      // Show loader
      var loader = document.querySelector(this.selectors.loader);
      if (loader) {
        loader.style.display = '';
      }

      // Reinitialize
      this.init();
    },

    destroy: function() {
      if (this.state.observer) {
        this.state.observer.disconnect();
        this.state.observer = null;
      }
    }
  };

  // ═══════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════
  function init() {
    var container = document.querySelector(CONFIG.selectors.container);
    if (!container) return;

    // Set first tab as active if not already set
    var firstTab = container.querySelector(CONFIG.selectors.tab);
    if (firstTab && !container.querySelector(CONFIG.selectors.tab + '.' + CONFIG.classes.active)) {
      switchTab(firstTab.dataset.tab);
    }

    // Initialize price sliders
    initPriceSliders();

    // Initialize infinite scroll
    InfiniteScroll.init();

    // Attach event listeners (only once)
    if (!window._achillesFilterInitialized) {
      document.addEventListener('click', handleClick);
      document.addEventListener('change', handleChange);
      document.addEventListener('keydown', handleKeydown);

      // Drag handlers for sheet
      document.addEventListener('mousedown', handleDragStart);
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchstart', handleDragStart, { passive: true });
      document.addEventListener('touchmove', handleDragMove, { passive: true });
      document.addEventListener('touchend', handleDragEnd);

      // Handle browser back/forward
      window.addEventListener('popstate', function() {
        fetchAndRenderCollection(window.location.href);
      });

      // Listen for collection reload to reset infinite scroll
      document.addEventListener('collection:reloaded', function() {
        InfiniteScroll.reset();
      });

      window._achillesFilterInitialized = true;
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
